import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Package, Truck, CheckCircle, Clock, XCircle, 
  RefreshCw, LogOut, Headphones, Bell, ChevronRight, Wifi, WifiOff
} from "lucide-react";
import { clearSessionToken } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OpsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [isConnected, setIsConnected] = useState(true);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const lastOrderCountRef = useRef(0);
  const pollIntervalRef = useRef(null);

  const fetchDashboard = useCallback(async (checkForNewOrders = false) => {
    try {
      const [dashRes, ordersRes] = await Promise.all([
        axios.get(`${API}/ops/dashboard`),
        axios.get(`${API}/ops/orders?status=confirmed`)
      ]);
      
      const newDashData = dashRes.data;
      const newOrders = ordersRes.data;
      
      // Check for new orders (polling-based real-time)
      if (checkForNewOrders && newOrders.length > 0) {
        const totalOrders = newDashData.total_orders || 0;
        
        if (lastOrderCountRef.current > 0 && totalOrders > lastOrderCountRef.current) {
          // New order detected!
          const newestOrder = newOrders[0];
          
          // Show toast notification
          toast.success(`New order from ${newestOrder.user_name}!`, {
            description: `${newestOrder.items?.length || 0} items • ₹${newestOrder.total_amount?.toFixed(2)}`,
            duration: 10000,
            action: {
              label: "View",
              onClick: () => navigate(`/ops/orders/${newestOrder.order_id}`)
            }
          });

          // Play notification sound
          playNotificationSound();

          // Show alert banner
          setNewOrderAlert({
            order_id: newestOrder.order_id,
            message: `New order from ${newestOrder.user_name} - ₹${newestOrder.total_amount?.toFixed(2)}`,
            items_count: newestOrder.items?.length || 0
          });
          setTimeout(() => setNewOrderAlert(null), 10000);
        }
        
        lastOrderCountRef.current = totalOrders;
      } else if (!checkForNewOrders) {
        lastOrderCountRef.current = newDashData.total_orders || 0;
      }
      
      setDashboardData(newDashData);
      setRecentOrders(newOrders.slice(0, 10));
      setIsConnected(true);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setIsConnected(false);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/ops/login");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = "sine";
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
      
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        osc2.connect(gainNode);
        osc2.frequency.value = 1000;
        osc2.type = "sine";
        osc2.start();
        osc2.stop(audioContext.currentTime + 0.2);
      }, 200);
    } catch (e) {
      console.log("Audio not available");
    }
  };

  useEffect(() => {
    // Check authentication
    const checkAuth = async () => {
      if (!user) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          if (response.data.role !== "operations_manager") {
            navigate("/ops/login");
            return;
          }
          setUser(response.data);
        } catch {
          navigate("/ops/login");
          return;
        }
      }
      // Initial fetch
      fetchDashboard(false);
      
      // Start polling every 5 seconds for real-time updates
      pollIntervalRef.current = setInterval(() => {
        fetchDashboard(true);
      }, 5000);
    };

    checkAuth();

    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user, navigate, fetchDashboard]);

  const handleLogout = async () => {
    // Stop polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    try {
      await axios.post(`${API}/auth/logout`);
      clearSessionToken();
      toast.success("Logged out successfully");
      navigate("/ops/login");
    } catch {
      clearSessionToken();
      navigate("/ops/login");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: "bg-yellow-500",
      processing: "bg-blue-500",
      dispatched: "bg-purple-500",
      delivered: "bg-green-500",
      cancelled: "bg-red-500"
    };
    return colors[status] || "bg-gray-500";
  };

  const getStatusIcon = (status) => {
    const icons = {
      confirmed: <Clock className="w-4 h-4" />,
      processing: <Package className="w-4 h-4" />,
      dispatched: <Truck className="w-4 h-4" />,
      delivered: <CheckCircle className="w-4 h-4" />,
      cancelled: <XCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* New Order Alert Banner */}
      {newOrderAlert && (
        <div 
          className="fixed top-0 left-0 right-0 z-[60] bg-green-500 text-white py-3 px-4 shadow-lg animate-pulse cursor-pointer"
          onClick={() => {
            navigate(`/ops/orders/${newOrderAlert.order_id}`);
            setNewOrderAlert(null);
          }}
          data-testid="new-order-alert"
        >
          <div className="container mx-auto max-w-4xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 animate-bounce" />
              <div>
                <p className="font-bold">New Order!</p>
                <p className="text-sm text-green-100">{newOrderAlert.message}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`bg-purple-600 text-white sticky ${newOrderAlert ? 'top-[52px]' : 'top-0'} z-50 shadow-lg transition-all`}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Headphones className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Operations Hub</h1>
              <p className="text-purple-200 text-xs">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Connection Status */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isConnected ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {isConnected ? (
                <Wifi className="w-4 h-4 text-green-300" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-300" />
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20"
              onClick={() => fetchDashboard(false)}
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Connection Status Banner */}
        {!isConnected && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center gap-2 text-yellow-800">
            <WifiOff className="w-4 h-4" />
            <span className="text-sm">Connection issue - retrying...</span>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="border-0 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-yellow-600">
                {dashboardData?.status_breakdown?.confirmed || 0}
              </p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="stat-processing">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {dashboardData?.status_breakdown?.processing || 0}
              </p>
              <p className="text-xs text-muted-foreground">Processing</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="stat-dispatched">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Truck className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {dashboardData?.status_breakdown?.dispatched || 0}
              </p>
              <p className="text-xs text-muted-foreground">Dispatched</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="stat-delivered">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600">
                {dashboardData?.status_breakdown?.delivered || 0}
              </p>
              <p className="text-xs text-muted-foreground">Delivered</p>
            </CardContent>
          </Card>
        </div>

        {/* Today's Summary */}
        <Card className="border-0 shadow-sm mb-6 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Today's Orders</p>
                <p className="text-3xl font-bold">{dashboardData?.today_orders || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-purple-100 text-sm">Needs Attention</p>
                <p className="text-3xl font-bold">{dashboardData?.pending_orders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Button 
            className="h-16 rounded-xl bg-white hover:bg-gray-50 text-gray-800 border shadow-sm"
            onClick={() => navigate("/ops/orders")}
            data-testid="view-all-orders-btn"
          >
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              <span>All Orders</span>
            </div>
          </Button>
          <Button 
            className="h-16 rounded-xl bg-white hover:bg-gray-50 text-gray-800 border shadow-sm"
            onClick={() => navigate("/ops/orders?status=confirmed")}
            data-testid="pending-orders-btn"
          >
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-yellow-600" />
              <span>Pending Orders</span>
            </div>
          </Button>
        </div>

        {/* Recent Pending Orders */}
        <Card className="border-0 shadow-sm" data-testid="recent-orders-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Pending Orders</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-purple-600"
                onClick={() => navigate("/ops/orders?status=confirmed")}
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {recentOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                <p>No pending orders!</p>
                <p className="text-sm">All caught up</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div 
                    key={order.order_id}
                    className="p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => navigate(`/ops/orders/${order.order_id}`)}
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">#{order.order_id.slice(-8)}</span>
                      <Badge className={`${getStatusColor(order.status)} text-white text-xs`}>
                        {getStatusIcon(order.status)}
                        <span className="ml-1 capitalize">{order.status}</span>
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{order.user_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm">{order.items?.length || 0} items</span>
                      <span className="font-semibold">₹{order.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live status indicator */}
        <div className="text-center mt-6">
          <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Auto-updating every 5 seconds
          </div>
        </div>
      </main>
    </div>
  );
}
